import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Delta } from './primitives';

/**
 * Page hero — the page's headline number as a Lumina KPI card.
 *
 * The page's one headline number sits in large, light metric type on a card
 * with the orange top-edge accent: the accent identifies "the number that
 * matters" without shouting a full orange fill at the reader. Actions render
 * as ordinary buttons on the card's right.
 */
export function Masthead({
  label,
  metric,
  delta,
  deltaSuffix,
  caption,
  date,
  action,
}: {
  label: string;
  metric: string;
  delta?: number | string | null;
  deltaSuffix?: string;
  caption?: string;
  date?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card kpi-accent kpi-accent-orange mb-section px-6 py-5">
      <div className="flex items-start justify-between gap-4">
        <span className="label">{label}</span>
        {date && <span className="label shrink-0 normal-case tracking-normal">{date}</span>}
      </div>

      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <div className="font-sans text-metric-sm font-light tabular text-ink md:text-metric">
            {metric}
          </div>
          {(delta !== undefined && delta !== null) || caption ? (
            <div className="mt-1.5 flex items-center gap-3 font-sans text-caption text-ink-secondary">
              {delta !== undefined && delta !== null && (
                <Delta value={delta} suffix={deltaSuffix} />
              )}
              {caption && <span>{caption}</span>}
            </div>
          ) : null}
        </div>
        {action && <div className="no-print flex flex-wrap gap-3">{action}</div>}
      </div>
    </div>
  );
}

const KPI_ACCENTS = ['kpi-accent-indigo', 'kpi-accent-orange', 'kpi-accent-green', 'kpi-accent-purple'];

/**
 * KPI card row — each metric on its own card with a coloured top-edge accent,
 * the number large and light, the label above in caption style.
 */
export function KpiRow({
  items,
  className,
}: {
  items: Array<{
    label: string;
    value: string;
    delta?: number | string | null;
    deltaSuffix?: string;
    hint?: string;
    href?: string;
  }>;
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <div
      className={clsx(
        'mb-section grid gap-4',
        items.length >= 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((item, i) => (
        <div key={item.label} className={clsx('card kpi-accent px-5 py-4', KPI_ACCENTS[i % KPI_ACCENTS.length])}>
          <div className="label">{item.label}</div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="metric">{item.value}</span>
            {item.delta !== undefined && <Delta value={item.delta} suffix={item.deltaSuffix} />}
          </div>
          {item.hint && <div className="mt-0.5 text-caption text-ink-tertiary">{item.hint}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * Section heading. Sits directly on the gradient ground; the content below
 * carries its own surface (tables and grids render as cards themselves).
 */
export function Section({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('mb-section', className)}>
      {(title || action) && (
        // Wraps (VIS-018/020): a long title beside a row of buttons pushed the
        // buttons off a tablet and widened a phone. The actions drop below.
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          {title && <h2 className="section-title min-w-0">{title}</h2>}
          {action && <div className="no-print max-w-full">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
